import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import Index from "./pages/Index";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Industries from "./pages/Industries";
import About from "./pages/About";
import Imprint from "./pages/Imprint";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import Configurator from "./pages/Configurator";
import HelpCenter from "./pages/HelpCenter";
import HelpArticle from "./pages/HelpArticle";

// Portal
import PortalLayout from "./components/portal/PortalLayout";
import PortalLogin from "./pages/portal/PortalLogin";
import ResetPassword from "./pages/portal/ResetPassword";
import Dashboard from "./pages/portal/Dashboard";
import MyModules from "./pages/portal/MyModules";
import Invoices from "./pages/portal/Invoices";
import PortalSettings from "./pages/portal/PortalSettings";
import Support from "./pages/portal/Support";
import CheckoutSuccess from "./pages/portal/CheckoutSuccess";

// Admin
import AdminProtectedRoute from "./components/admin/AdminProtectedRoute";
import AdminLayout from "./components/admin/AdminLayout";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminCustomers from "./pages/admin/AdminCustomers";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <ConsentProvider>
          <CookieBanner />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/features" element={<Features />} />
            <Route path="/preise" element={<Pricing />} />
            <Route path="/branchen" element={<Industries />} />
            <Route path="/ueber-uns" element={<About />} />
            <Route path="/impressum" element={<Imprint />} />
            <Route path="/datenschutz" element={<Privacy />} />
            <Route path="/agb" element={<Terms />} />
            <Route path="/konfigurator" element={<Configurator />} />
            <Route path="/hilfe" element={<HelpCenter />} />
            <Route path="/hilfe/:slug" element={<HelpArticle />} />

            {/* Auth */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Portal (protected) */}
            <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="checkout-success" element={<CheckoutSuccess />} />
              <Route path="modules" element={<MyModules />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="settings" element={<PortalSettings />} />
              <Route path="support" element={<Support />} />
            </Route>

            {/* Admin (protected, admin role required) */}
            <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
              <Route index element={<Navigate to="/admin/orders" replace />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="customers" element={<AdminCustomers />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ConsentProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
